import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PageTitle, 
  Card, 
  Spinner, 
  EmptyState,
  Alert,
  Modal
} from '../components';
import Button from 'react-bootstrap/Button';
import { 
  FaDesktop, 
  FaPlus, 
  FaEllipsisH, 
  FaTrash, 
  FaEdit, 
  FaGlobe, 
  FaSearch,
  FaExclamationCircle,
  FaFilter,
  FaSitemap,
  FaGamepad
} from 'react-icons/fa';
import siteService from '../services/siteService';
import { formatRelativeTime } from '../utils/formatters';
import BatchImportModal from '../components/BatchImportModal';

const SitesPage = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // 添加新站点相关状态
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [newSite, setNewSite] = useState({
    name: '',
    url: '',
    checkFrequency: 'daily',
    checkSitemap: true,
    sitemapUrl: '',
  });
  const [addSiteLoading, setAddSiteLoading] = useState(false);
  const [addSiteError, setAddSiteError] = useState('');
  
  // 批量导入相关状态
  const [showBatchImport, setShowBatchImport] = useState(false);
  
  // 添加游戏站点预设
  const [showAddGameSiteModal, setShowAddGameSiteModal] = useState(false);
  const [gameSitePresets, setGameSitePresets] = useState([
    {
      name: 'Steam 新游戏',
      url: 'https://store.steampowered.com',
      sitemapUrl: 'https://store.steampowered.com/sitemap_games_1.xml',
      checkFrequency: 'daily',
      checkSitemap: true
    },
    {
      name: '3DM游戏网',
      url: 'https://www.3dmgame.com',
      sitemapUrl: 'https://www.3dmgame.com/sitemap.xml',
      checkFrequency: 'daily',
      checkSitemap: true
    },
    {
      name: 'IGN游戏',
      url: 'https://www.ign.com',
      sitemapUrl: 'https://www.ign.com/sitemaps/articles/index.xml',
      checkFrequency: 'daily',
      checkSitemap: true
    }
  ]);
  const [selectedGameSite, setSelectedGameSite] = useState(null);

  // 获取站点列表函数
  const fetchSites = async () => {
    try {
      setLoading(true);
      const data = await siteService.getSites();
      setSites(data);
    } catch (err) {
      console.error('获取站点列表失败:', err);
      setError('获取站点列表失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  // 获取站点列表
  useEffect(() => {
    fetchSites();
  }, []);

  // 处理搜索
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // 筛选站点
  const filteredSites = sites.filter(site => 
    site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 确认删除站点
  const confirmDelete = (site) => {
    setSiteToDelete(site);
    setShowDeleteModal(true);
  };

  // 执行删除站点
  const handleDelete = async () => {
    if (!siteToDelete) return;
    
    try {
      setDeleteLoading(true);
      await siteService.deleteSite(siteToDelete._id);
      setSites(sites.filter(site => site._id !== siteToDelete._id));
      setShowDeleteModal(false);
      setSiteToDelete(null);
    } catch (err) {
      setError('删除站点失败: ' + (err.response?.data?.message || '未知错误'));
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // 处理新站点表单变更
  const handleNewSiteChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewSite({
      ...newSite,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // 添加新站点
  const handleAddSite = async (e) => {
    e.preventDefault();
    
    try {
      setAddSiteError('');
      setAddSiteLoading(true);
      
      // 表单验证
      if (!newSite.name.trim()) {
        setAddSiteError('请输入站点名称');
        setAddSiteLoading(false);
        return;
      }
      
      if (!newSite.url.trim()) {
        setAddSiteError('请输入站点URL');
        setAddSiteLoading(false);
        return;
      }
      
      // 创建新站点
      const createdSite = await siteService.createSite(newSite);
      
      // 更新站点列表
      setSites([...sites, createdSite]);
      
      // 重置表单并关闭模态框
      setNewSite({
        name: '',
        url: '',
        checkFrequency: 'daily',
        checkSitemap: true,
        sitemapUrl: '',
      });
      setShowAddSiteModal(false);
      
    } catch (err) {
      setAddSiteError('创建站点失败: ' + (err.message || '未知错误'));
    } finally {
      setAddSiteLoading(false);
    }
  };

  // 处理批量导入成功
  const handleBatchImportSuccess = () => {
    // 刷新站点列表
    fetchSites();
  };

  // 添加游戏站点预设
  const handleAddGameSite = async (preset) => {
    try {
      setAddSiteLoading(true);
      setAddSiteError('');
      
      const createdSite = await siteService.createSite(preset);
      
      // 更新站点列表
      setSites([...sites, createdSite]);
      setShowAddGameSiteModal(false);
      
    } catch (err) {
      setAddSiteError('创建游戏站点失败: ' + (err.message || '未知错误'));
    } finally {
      setAddSiteLoading(false);
    }
  };
  
  // 快速添加自定义游戏站点
  const handleAddCustomGameSite = async () => {
    if (!selectedGameSite) return;
    
    try {
      await handleAddGameSite(selectedGameSite);
    } catch (err) {
      console.error('添加游戏站点失败:', err);
    }
  };

  // 如果正在加载，显示加载中状态
  if (loading) {
    return <Spinner fullPage text="加载站点列表..." />;
  }

  return (
    <div className="sites-page">
      <PageTitle 
        title="监控站点"
        subtitle="管理您的网站监控"
        breadcrumbs={[
          { text: '仪表盘', link: '/dashboard' },
          { text: '监控站点', link: '/sites' }
        ]}
      >
        <div className="d-flex">
          <Button 
            variant="outline-success" 
            className="me-2"
            onClick={() => setShowAddGameSiteModal(true)}
          >
            <FaGamepad className="me-1" /> 添加游戏站点
          </Button>
          <Button 
            variant="outline-primary" 
            className="me-2"
            onClick={() => setShowBatchImport(true)}
          >
            <i className="bi bi-cloud-upload me-1"></i> 批量导入
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setShowAddSiteModal(true)}
          >
            <i className="bi bi-plus-lg me-1"></i> 添加站点
          </Button>
        </div>
      </PageTitle>
      
      {error && (
        <Alert 
          type="danger"
          message={error}
          dismissible={true}
          onClose={() => setError('')}
        />
      )}
      
      {/* 搜索和筛选 */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <FaSearch />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="搜索站点名称或URL..."
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm && (
              <button 
                className="btn btn-outline-secondary" 
                type="button"
                onClick={() => setSearchTerm('')}
              >
                清除
              </button>
            )}
          </div>
        </div>
        
        <div className="col-md-6 text-md-end mt-3 mt-md-0">
          <span className="text-muted me-2">共 {sites.length} 个站点</span>
        </div>
      </div>
      
      {/* 站点列表 */}
      {filteredSites.length > 0 ? (
        <div className="row">
          {filteredSites.map(site => (
            <div key={site._id} className="col-md-6 col-lg-4 mb-4">
              <Card className="h-100 site-card">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h5 className="card-title text-truncate mb-0">
                    <FaGlobe className="text-primary me-2" />
                    {site.name}
                  </h5>
                  <div className="dropdown">
                    <button className="btn btn-sm btn-light" type="button" id={`dropdown-${site._id}`} data-bs-toggle="dropdown" aria-expanded="false">
                      <FaEllipsisH />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={`dropdown-${site._id}`}>
                      <li>
                        <Link className="dropdown-item" to={`/sites/${site._id}`}>
                          <FaDesktop className="me-2 text-primary" />
                          查看详情
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to={`/sites/${site._id}/edit`}>
                          <FaEdit className="me-2 text-success" />
                          编辑站点
                        </Link>
                      </li>
                      <li>
                        <button className="dropdown-item text-danger" onClick={() => confirmDelete(site)}>
                          <FaTrash className="me-2" />
                          删除站点
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <p className="text-muted small mb-2 text-truncate">
                  <a href={site.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-muted">
                    {site.url}
                  </a>
                </p>
                
                <div className="d-flex justify-content-between mb-2">
                  <span className="badge bg-primary">
                    {site.checkFrequency} 监控
                  </span>
                  <span className="badge bg-light text-dark">
                    {site.checkPages?.length || 0} 个页面
                  </span>
                </div>
                
                {site.checkSitemap && (
                  <div className="badge bg-info text-white me-2 mb-2">
                    <FaSitemap className="me-1" /> Sitemap监控
                  </div>
                )}
                
                <div className="mt-3">
                  <small className="text-muted">
                    最后检查: {site.lastChecked ? formatRelativeTime(site.lastChecked) : '未检查'}
                  </small>
                </div>
                
                <div className="mt-2">
                  <Link to={`/sites/${site._id}`} className="btn btn-sm btn-outline-primary mt-2">
                    查看详情
                  </Link>
                </div>
              </Card>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState 
          type="no-data"
          title="暂无监控站点"
          message="您尚未添加任何监控站点"
          action={
            <button onClick={() => setShowAddSiteModal(true)} className="btn btn-primary">
              <FaPlus className="me-2" />
              添加站点
            </button>
          }
        />
      )}
      
      {/* 删除站点确认模态框 */}
      <Modal
        show={showDeleteModal}
        title="确认删除"
        onClose={() => setShowDeleteModal(false)}
        footer={
          <>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteLoading}
            >
              取消
            </button>
            <button 
              className="btn btn-danger" 
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? '删除中...' : '确认删除'}
            </button>
          </>
        }
      >
        <p>您确定要删除站点 <strong>{siteToDelete?.name}</strong> 吗？</p>
        <p className="text-danger">此操作无法撤销，所有相关的监控记录将被永久删除。</p>
      </Modal>
      
      {/* 添加站点模态框 */}
      <Modal
        show={showAddSiteModal}
        title="添加监控站点"
        size="lg"
        onClose={() => setShowAddSiteModal(false)}
        footer={
          <>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowAddSiteModal(false)}
              disabled={addSiteLoading}
            >
              取消
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleAddSite}
              disabled={addSiteLoading}
            >
              {addSiteLoading ? '添加中...' : '添加站点'}
            </button>
          </>
        }
      >
        {addSiteError && (
          <Alert
            type="danger"
            message={addSiteError}
            dismissible={true}
            onClose={() => setAddSiteError('')}
          />
        )}
        
        <form onSubmit={handleAddSite}>
          <div className="mb-3">
            <label htmlFor="name" className="form-label">站点名称*</label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={newSite.name}
              onChange={handleNewSiteChange}
              placeholder="例如：我的博客"
              required
            />
          </div>
          
          <div className="mb-3">
            <label htmlFor="url" className="form-label">站点URL*</label>
            <input
              type="url"
              className="form-control"
              id="url"
              name="url"
              value={newSite.url}
              onChange={handleNewSiteChange}
              placeholder="例如：https://example.com"
              required
            />
            <div className="form-text">请输入完整URL，包含http://或https://</div>
          </div>
          
          <div className="mb-3">
            <label htmlFor="checkFrequency" className="form-label">监控频率</label>
            <select
              className="form-select"
              id="checkFrequency"
              name="checkFrequency"
              value={newSite.checkFrequency}
              onChange={handleNewSiteChange}
            >
              <option value="hourly">每小时</option>
              <option value="daily">每天</option>
              <option value="weekly">每周</option>
            </select>
          </div>
          
          <div className="mb-3">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="checkSitemap"
                name="checkSitemap"
                checked={newSite.checkSitemap}
                onChange={handleNewSiteChange}
              />
              <label className="form-check-label" htmlFor="checkSitemap">
                监控Sitemap变化
              </label>
            </div>
            <div className="form-text">启用后，系统将自动检测网站Sitemap中的页面变化</div>
          </div>
          
          {newSite.checkSitemap && (
            <div className="mb-3">
              <label htmlFor="sitemapUrl" className="form-label">Sitemap URL (可选)</label>
              <input
                type="url"
                className="form-control"
                id="sitemapUrl"
                name="sitemapUrl"
                value={newSite.sitemapUrl}
                onChange={handleNewSiteChange}
                placeholder="例如：https://example.com/sitemap.xml"
              />
              <div className="form-text">如不填写，系统将尝试使用 网站URL/sitemap.xml</div>
            </div>
          )}
        </form>
      </Modal>
      
      {/* 游戏站点选择模态框 */}
      <Modal
        show={showAddGameSiteModal}
        onHide={() => setShowAddGameSiteModal(false)}
        title="添加游戏站点监控"
        footer={
          <>
            <Button 
              variant="secondary" 
              onClick={() => setShowAddGameSiteModal(false)}
            >
              取消
            </Button>
            <Button 
              variant="primary" 
              onClick={handleAddCustomGameSite}
              disabled={!selectedGameSite || addSiteLoading}
            >
              {addSiteLoading ? '添加中...' : '添加所选站点'}
            </Button>
          </>
        }
      >
        <div className="mb-3">
          <p className="text-muted">选择预设游戏站点或自定义添加游戏站点</p>
          {addSiteError && <Alert type="danger" message={addSiteError} />}
          
          <div className="game-site-presets mt-3">
            {gameSitePresets.map((preset, index) => (
              <div 
                key={index} 
                className={`game-site-preset-card p-3 mb-2 border rounded ${selectedGameSite === preset ? 'border-primary bg-light' : ''}`}
                onClick={() => setSelectedGameSite(preset)}
              >
                <div className="d-flex align-items-center">
                  <FaGamepad className="me-2 text-primary" size={20} />
                  <div>
                    <h6 className="mb-1">{preset.name}</h6>
                    <small className="text-muted d-block">
                      {preset.url}
                    </small>
                    <small className="text-muted d-block">
                      <FaSitemap className="me-1" size={12} /> 
                      {preset.sitemapUrl}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-3">
            <h6>添加自定义游戏站点</h6>
            <div className="mb-3">
              <label htmlFor="customName" className="form-label">站点名称</label>
              <input 
                type="text" 
                className="form-control"
                id="customName"
                placeholder="例如: 我的游戏站点"
                value={newSite.name}
                onChange={(e) => setNewSite({...newSite, name: e.target.value})}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="customUrl" className="form-label">站点URL</label>
              <input 
                type="text" 
                className="form-control"
                id="customUrl"
                placeholder="例如: https://www.example.com"
                value={newSite.url}
                onChange={(e) => setNewSite({...newSite, url: e.target.value})}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="customSitemapUrl" className="form-label">Sitemap URL</label>
              <input 
                type="text" 
                className="form-control"
                id="customSitemapUrl"
                placeholder="例如: https://www.example.com/sitemap.xml"
                value={newSite.sitemapUrl}
                onChange={(e) => setNewSite({...newSite, sitemapUrl: e.target.value})}
              />
            </div>
            <Button 
              variant="outline-primary"
              onClick={() => setSelectedGameSite(newSite)}
              disabled={!newSite.name || !newSite.url || !newSite.sitemapUrl}
            >
              使用自定义设置
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* 批量导入模态窗口 */}
      <BatchImportModal
        show={showBatchImport}
        onHide={() => setShowBatchImport(false)}
        onSuccess={handleBatchImportSuccess}
      />
    </div>
  );
};

export default SitesPage; 